import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { DealerSearchContext } from '@repo/contexts/DealerSearchContext';
import SanityImage from 'gatsby-plugin-sanity-image';
import Button from '@repo/ui-components/button/Button';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import BlockContent from '../../BlockContent';
import breakpoints from '../../../breakpoints';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  max-width: ${breakpoints.md};
  margin: 0 auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  overflow: hidden;
`;

const ImageContainer = styled.div`
  text-align: center;

  img {
    width: auto;
    max-width: 100%;
  }
`;

const ContentSection = styled.div`
  flex: 1;
  padding: 1rem;
  text-align: center;

  ul,
  ol {
    text-align: left;
  }
`;

function BlockBuilderContentCard({ value, getInternalLink }) {
  const dealerSearchContext = useContext(DealerSearchContext);
  const { tt } = useUiComponentsInternationalContext();

  const dealerTypeSelected = !!dealerSearchContext?.storedDealer;
  const isCOD = dealerTypeSelected && dealerSearchContext.storedDealer?.isCOS;

  let image = value?.media;
  let contentSection = value?.contentSection?.text;

  if (dealerTypeSelected) {
    if (value.useAlternateIDContent && !isCOD) {
      image = value?.mediaID;
      contentSection = value?.contentSectionID?.text;
    } else if (value.useAlternateCODContent && isCOD) {
      image = value?.mediaCOD;
      contentSection = value?.contentSectionCOD?.text;
    }
  }

  return (
    <Container>
      <InnerContainer>
        {image?.asset && (
          <ImageContainer>
            <SanityImage
              asset={image.asset}
              hotspot={image.hotspot}
              crop={image.crop}
              alt={image.alt || ''}
              width={768}
              height={parseInt((768 / 21) * 9, 10)}
            />
          </ImageContainer>
        )}
        <ContentSection>
          {contentSection?.length && (
            <BlockContent
              blocks={contentSection}
              getInternalLink={getInternalLink}
            />
          )}
        </ContentSection>
        {!dealerTypeSelected && value?.enableDealerSelectButton && (
          <Button onClick={dealerSearchContext?.setDealerSearchActive}>
            {value.dealerSearchButtonText?.length
              ? value.dealerSearchButtonText
              : tt(
                  s => s.blockBuilderContentCardComponent.searchForDealerButton
                )}
          </Button>
        )}
      </InnerContainer>
    </Container>
  );
}

BlockBuilderContentCard.propTypes = {
  value: PropTypes.shape({
    media: PropTypes.shape({
      asset: PropTypes.shape({}),
      hotspot: PropTypes.shape({}),
      crop: PropTypes.shape({}),
      alt: PropTypes.string,
    }),
    contentSection: PropTypes.shape({
      text: PropTypes.string,
    }),
    enableDealerSelectButton: PropTypes.bool,
    dealerSearchButtonText: PropTypes.string,
    useAlternateIDContent: PropTypes.bool,
    mediaID: PropTypes.shape({
      asset: PropTypes.shape({}),
      hotspot: PropTypes.shape({}),
      crop: PropTypes.shape({}),
      alt: PropTypes.string,
    }),
    contentSectionID: PropTypes.shape({
      text: PropTypes.string,
    }),
    useAlternateCODContent: PropTypes.bool,
    mediaCOD: PropTypes.shape({
      asset: PropTypes.shape({}),
      hotspot: PropTypes.shape({}),
      crop: PropTypes.shape({}),
      alt: PropTypes.string,
    }),
    contentSectionCOD: PropTypes.shape({
      text: PropTypes.string,
    }),
  }).isRequired,
  getInternalLink: PropTypes.func.isRequired,
};

export default BlockBuilderContentCard;
